/**
 * **变参行拖拽排序** —— 从 Playground.vue 抽出来。
 *
 * ## 这个文件负责什么
 *
 * 把第 `from` 个实例挪到第 `to` 个。
 *
 * 实例是用**序号寻址**的（`args.0` / `args.1` …），所以「换顺序」的实质
 * 不是挪数据，而是**改写挂在这些实例上的连线的 `targetHandle`** ——
 * 值跟着序号走。
 *
 * ★ 只重排**已连接**的实例，空槽不参与。实例数量是由「最后一个已连实例的
 * 序号 + 2」推导的（见 `useProjection` 里的 `repeaterCountFor`），
 * 让空槽参与置换会长出一行没有连线的变参行。
 *
 * ## ⚠️ 抽出来的方式：函数体逐字不动
 */
import { nextTick } from 'vue'
/* ⚠️ 结构类型，不是引擎的类型 —— 换引擎时这一行不用改（`flowTypes.ts`） */
import type { FlowEdge as Edge } from '@/graph/flowTypes'
import {
  moveIndexMap,
  repeaterCount,
  repeaterHandle,
  repeaterIndexOf,
} from '@/node/rows'
import { sameHandle } from '@/graph/connectionRules'
import type { GraphNodeModel, RepeaterRow } from '@/types/node'
import type { FlowStoreApi } from '@/graph/flowStore'

export interface RepeaterReorderCtx {
  edges: { value: Edge[] }
  flowStore: { value: FlowStoreApi | null }
  modelOf: (id: string) => GraphNodeModel | undefined
  commit: (label: string, coalesceMs?: number) => void
  showNotice: (message: string) => void
}

export function useRepeaterReorder(ctx: RepeaterReorderCtx) {
  const { edges, flowStore, modelOf, commit, showNotice } = ctx

/* ── 变参行拖拽排序 ─────────────────────────────────────────── */

/**
 * 把第 from 个实例挪到第 to 个。
 *
 * 实例是用序号寻址的（args.0 / args.1 …），所以「换顺序」的实质
 * 是**改写挂在这些实例上的连线的 targetHandle** —— 值跟着序号走。
 */
function onRepeaterReorder(
  nodeId: string,
  rowId: string,
  from: number,
  to: number,
) {
  const row = modelOf(nodeId)?.rows.find((r) => r.id === rowId) as
    | RepeaterRow
    | undefined
  if (!row) return

  /*
   * 走最小结构类型而不是直接 edges.value.map(...)：
   * Vue Flow 的 Edge 泛型太深，`.map` 会触发 TS2589（实例化过深）。
   */
  type LooseEdge = {
    source: string
    target: string
    sourceHandle?: string | null
    targetHandle?: string | null
    [key: string]: unknown
  }

  commit('调整变参顺序')
  const total = repeaterCount(row)
  const list = edges.value as unknown as LooseEdge[]

  /*
   * ★ 只重排**已连接**的实例，空槽不参与。
   *
   * 为什么这一点很关键：实例数量是由「最后一个已连实例的序号 + 2」推导的
   * （见连接态同步里的 repeaterCountFor）。如果让空槽也参与置换，
   * 把第 1 行拖到末尾的空行上，就会把连线挪到末尾、首行空出来 ——
   * lastConnected 因此变大、列表又长出一行，
   * 表现出来就是「上边凭空多了一个没连线的变参行」。
   */
  const slots: number[] = []
  for (let i = 0; i < total; i += 1) {
    const handle = repeaterHandle(row, i)
    if (list.some((e) => e.target === nodeId && sameHandle(e.targetHandle, handle))) {
      slots.push(i)
    }
  }

  const count = slots.length
  const fromPos = slots.indexOf(from)
  if (count < 2 || fromPos < 0) return

  // 落点换算到「已连接序列」里的位置：数一数 to 之前有几个已连接的；
  // 落在最后一个已连接之后 → 就是末尾
  const toPos = Math.max(
    0,
    Math.min(slots.filter((i) => i < to).length, count - 1),
  )
  if (toPos === fromPos) return

  const positionOf = moveIndexMap(count, fromPos, toPos)
  /** 旧实例序号 → 重排后应该去的实例序号 */
  const targetSlotOf = new Map<number, number>()
  for (let p = 0; p < count; p += 1) {
    targetSlotOf.set(slots[p], slots[positionOf.get(p) ?? p])
  }

  edges.value = list.map((edge) => {
    if (edge.target !== nodeId) return edge
    const at = repeaterIndexOf(row, edge.targetHandle ?? '')
    if (at < 0) return edge
    const next = targetSlotOf.get(at)
    if (next === undefined || next === at) return edge
    return { ...edge, targetHandle: repeaterHandle(row, next) }
  }) as unknown as Edge[]

  // row.connected 由下面的 watchEffect 从边推导，这里不需要手动同步
  nextTick(() => flowStore.value?.updateNodeInternals([nodeId]))
  showNotice(`已把第 ${from + 1} 项移到第 ${toPos + 1} 项`)
}

  return { onRepeaterReorder }
}
